import React from 'react';
import { Box } from 'pounce';
import { useTabsContext } from './Tabs';

export type TabPanelProps = {
  /**
   * Whether the tabs should only load when they are activated
   * @default false
   * */
  lazy?: boolean;

  /**
   * Unmounts the tab when it's not active, so that recurring visits will re-mount. Only applies to
   * tab panels that are lazy loaded (i.e. `lazy=true`)
   * @default false
   */
  unmountWhenInactive?: boolean;

  /**
   * Whether that tab should start as hidden on initial mount. This is `true` by default to allow for React calculations
   * to execute. Set it to `false` if you need to make DOM-related calculations during mount-time
   */
  hidden?: boolean;
  children?: React.ReactNode;
};

type PrivateTabPanelProps = TabPanelProps & {
  /**
   * @ignore
   * private internal index of the tab panel in relation to its siblings. Passed automatically from
   * the `TabPanels` component
   */
  index: number;

  /**
   * @ignore
   * The currently selected tab index passed from TabPanels
   */
  value?: number;

  /**
   * @ignore
   * The tab index passed from TabPanels
   */
  tabIndex?: number;
};

const TabPanel: React.FC<PrivateTabPanelProps> = ({
  index,
  value,
  tabIndex,
  lazy = false,
  unmountWhenInactive = false,
  children,
  ...rest
}) => {
  const { selectedIndex } = useTabsContext();
  const activeIndex = value !== undefined ? value : selectedIndex;
  const currentIndex = tabIndex !== undefined ? tabIndex : index;

  const isActive = activeIndex === currentIndex;
  const wasActiveRef = React.useRef(isActive);

  React.useEffect(() => {
    if (isActive) {
      wasActiveRef.current = true;
    }
  }, [isActive]);

  const shouldRender = !lazy || isActive || (wasActiveRef.current && !unmountWhenInactive);

  // Only render if this is the active tab or if lazy loading conditions are met
  if (!isActive && lazy && !shouldRender) {
    return null;
  }

  return (
    <Box
      tabIndex={-1}
      outline="none"
      role="tabpanel"
      aria-labelledby={`tab-${currentIndex}`}
      id={`tabpanel-${currentIndex}`}
      hidden={!isActive}
      {...rest}
    >
      {shouldRender ? children : null}
    </Box>
  );
};

export default TabPanel as React.FC<TabPanelProps>;
